import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { userSchema } from '../schemas/userSchema';
import { Input, Select, Button } from './ui';
import api from '../api';
import { toast } from '../utils/toast';

const UserForm = ({ onUserCreated, onCancel }) => {
  const [companies, setCompanies] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(userSchema),
    defaultValues: {
      name: '',
      email: '',
      role: 'Client',
      companyId: '',
      password: '',
    },
  });

  const watchedRole = watch('role');

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await api.get('/companies');
        setCompanies(response.data.data || response.data);
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };

    fetchCompanies();
  }, []);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...data,
        clerkId: `clerk_${Date.now()}`,
      };

      // Only include companyId for Client role
      if (data.role !== 'Client') {
        delete payload.companyId;
      }

      const response = await api.post('/users', payload);
      toast.success('User created successfully');
      onUserCreated?.(response.data);
      reset();
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to create user';
      const errorDetails = error.response?.data?.details;
      if (errorDetails && Array.isArray(errorDetails)) {
        toast.error(`${errorMsg}: ${errorDetails.join(', ')}`);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleOptions = [
    { value: 'Admin', label: 'Admin' },
    { value: 'Manager', label: 'Manager' },
    { value: 'Technician', label: 'Technician' },
    { value: 'Client', label: 'Client' },
  ];

  return (
    <div className="mb-4 bg-slate-800 rounded-lg p-4 md:p-6 shadow-md border border-slate-700">
      <h3 className="mb-4 text-lg font-semibold text-white">Create New User</h3>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Name"
            required
            error={errors.name?.message}
            {...register('name')}
          />

          <Input
            label="Email"
            type="email"
            required
            error={errors.email?.message}
            {...register('email')}
          />

          <div>
            <Input
              label="Password"
              type="password"
              required
              error={errors.password?.message}
              {...register('password')}
            />
            <p className="text-xs text-slate-500 mt-1">
              Min 10 chars, uppercase, lowercase, number, special character
            </p>
          </div>

          <Select
            label="Role"
            required
            options={roleOptions}
            placeholder=""
            error={errors.role?.message}
            {...register('role')}
          />

          {watchedRole === 'Client' && (
            <Select
              label="Company"
              required
              options={companies.map((c) => ({ value: c.id.toString(), label: c.name }))}
              placeholder="Select a Company"
              error={errors.companyId?.message}
              {...register('companyId')}
            />
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          {onCancel && (
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" loading={isSubmitting}>
            Create User
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;
